/**
 * WTBTTForestJourneyMap — Book 2 Interactive Chapter Map
 *
 * Exact functional mirror of ForestJourneyMap (Book 1).
 * Behaviour is identical — only the chapter data, colours, and title differ.
 *
 * Behaviour matches Book 1 exactly:
 *  - Clicking the current chapter navigates immediately with ?spread=1
 *  - Clicking another chapter: walking-footprints animation plays, then navigate with ?spread=1
 *  - Static paw-print trail: each 🐾 has a blue glowing halo + blur fade-in (matches Book 1)
 *  - Walking footprints: golden glow, same timing / easing as Book 1
 *  - 20 confetti pieces falling from top (same params as Book 1)
 *  - Disabled buttons during walk animation
 *
 * Props:
 *   currentChapter    — which chapter (7–12) to highlight (default: 7)
 *   completedChapters — array of completed chapter IDs
 *   token             — optional access token forwarded in navigation URL
 *   onClose           — called when "Back to Contents" is clicked
 */

import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Home } from "lucide-react";

// ── Chapter location data ──────────────────────────────────────────────────────

interface ChapterLocation {
  id: number;
  emoji: string;
  title: string;
  subtitle: string;
  route: string;
  position: { x: string; y: string };
}

/**
 * Identical layout to Book 1 (same x/y grid: left/right top, center middle, left/right bottom).
 * Chapters 7–12 map 1-for-1 onto the positions used by chapters 1–6.
 */
const CHAPTER_LOCATIONS: ChapterLocation[] = [
  {
    id: 7,
    emoji: "👂",
    title: "Chapter 7",
    subtitle: "The Listening Trail",
    route: "/book/wtbtt/chapter/7/story",
    position: { x: "20%", y: "15%" },
  },
  {
    id: 8,
    emoji: "🪨",
    title: "Chapter 8",
    subtitle: "Waiting with a Plan",
    route: "/book/wtbtt/chapter/8/story",
    position: { x: "80%", y: "15%" },
  },
  {
    id: 9,
    emoji: "🎨",
    title: "Chapter 9",
    subtitle: "The Oops Banner",
    route: "/book/wtbtt/chapter/9/story",
    position: { x: "50%", y: "35%" },
  },
  {
    id: 10,
    emoji: "🏗️",
    title: "Chapter 10",
    subtitle: "The Tower Trouble",
    route: "/book/wtbtt/chapter/10/story",
    position: { x: "50%", y: "55%" },
  },
  {
    id: 11,
    emoji: "🪟",
    title: "Chapter 11",
    subtitle: "The Mirror Maze",
    route: "/book/wtbtt/chapter/11/story",
    position: { x: "20%", y: "75%" },
  },
  {
    id: 12,
    emoji: "🎪",
    title: "Chapter 12",
    subtitle: "The Festival Finale",
    route: "/book/wtbtt/chapter/12/story",
    position: { x: "80%", y: "75%" },
  },
];

// ── Props ──────────────────────────────────────────────────────────────────────

interface WTBTTForestJourneyMapProps {
  currentChapter?: number;
  completedChapters?: number[];
  token?: string | null;
  onClose?: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function WTBTTForestJourneyMap({
  currentChapter = 7,
  completedChapters = [],
  token,
  onClose,
}: WTBTTForestJourneyMapProps) {
  const navigate = useNavigate();
  const [hoveredChapter, setHoveredChapter] = useState<number | null>(null);
  const [isWalking, setIsWalking] = useState(false);
  const [walkingPath, setWalkingPath] = useState<{ from: number; to: number } | null>(null);

  /**
   * Build navigation URL — matches Book 1 pattern exactly (?spread=1).
   * Also appends token if present (Book 2 needs token to pass through).
   */
  const buildUrl = (route: string) => {
    const base = `${route}?spread=1`;
    return token ? `${base}&token=${token}` : base;
  };

  /**
   * Animated walking footprints before navigation — identical logic to Book 1.
   *
   * Steps:
   * 1. If clicking current chapter → navigate immediately (same as Book 1)
   * 2. Otherwise: start walk animation, wait for distance-based duration, pause 1.2s, navigate
   */
  const handleChapterClick = async (route: string, chapterId: number) => {
    // Clicking current chapter: navigate immediately (no animation)
    if (chapterId === currentChapter) {
      navigate(buildUrl(route));
      return;
    }

    setIsWalking(true);
    setWalkingPath({ from: currentChapter, to: chapterId });

    console.log(`🐾 Walking animation: Chapter ${currentChapter} → Chapter ${chapterId}`);

    // Same timing formula as Book 1: 800ms base + 400ms per chapter step
    const distance = Math.abs(chapterId - currentChapter);
    const animationDuration = 800 + distance * 400;

    await new Promise<void>((r) => setTimeout(r, animationDuration));

    console.log(`🎯 Footprints arrived at Chapter ${chapterId}! Pausing before navigation...`);
    await new Promise<void>((r) => setTimeout(r, 1200)); // 1.2s pause — same as Book 1

    console.log(`📍 Map navigation: Starting Chapter ${chapterId}`);
    navigate(buildUrl(route));

    setIsWalking(false);
    setWalkingPath(null);
  };

  const handleHomeClick = () => {
    if (onClose) onClose();
    else navigate("/book/wtbtt");
  };

  /**
   * Calculate walking footprint positions — identical algorithm to Book 1.
   * Uses 0-based array indices, direction ±1, 6 prints per segment, 80ms delay between each.
   */
  const getWalkingFootprints = () => {
    if (!walkingPath) return [];

    // Convert chapter IDs to 0-based array indices
    const start = CHAPTER_LOCATIONS.findIndex((c) => c.id === walkingPath.from);
    const end   = CHAPTER_LOCATIONS.findIndex((c) => c.id === walkingPath.to);
    if (start === -1 || end === -1) return [];

    const direction = end > start ? 1 : -1;
    const steps = Math.abs(end - start);

    const footprints: { x: number; y: number; angle: number; delay: number }[] = [];

    for (let step = 0; step < steps; step++) {
      const curIdx  = start + step * direction;
      const nextIdx = curIdx + direction;
      if (nextIdx < 0 || nextIdx >= CHAPTER_LOCATIONS.length) continue;

      const cur  = CHAPTER_LOCATIONS[curIdx]!;
      const nxt  = CHAPTER_LOCATIONS[nextIdx]!;
      const x1   = parseFloat(cur.position.x);
      const y1   = parseFloat(cur.position.y);
      const x2   = parseFloat(nxt.position.x);
      const y2   = parseFloat(nxt.position.y);
      const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);

      const numPrintsPerSegment = 6; // matches Book 1
      for (let i = 0; i <= numPrintsPerSegment; i++) {
        const progress = i / numPrintsPerSegment;
        footprints.push({
          x: x1 + (x2 - x1) * progress,
          y: y1 + (y2 - y1) * progress,
          angle,
          delay: (step * numPrintsPerSegment + i) * 0.08, // 80ms per print — same as Book 1
        });
      }
    }

    return footprints;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-teal-50 to-blue-100 relative overflow-hidden">

      {/* Back to Contents button — top-left, same position as Book 1 */}
      <div className="fixed top-6 left-6 z-50">
        <Button
          onClick={handleHomeClick}
          variant="outline"
          size="lg"
          className="bg-white/90 backdrop-blur-sm border-sky-200 text-sky-700 hover:bg-sky-50 shadow-lg"
        >
          <Home className="w-5 h-5 mr-2" />
          Back to Contents
        </Button>
      </div>

      {/* Confetti / balloon celebration — identical to Book 1 (20 items, same animation params) */}
      <motion.div
        className="fixed inset-0 pointer-events-none z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-4xl"
            style={{ left: `${Math.random() * 100}%`, top: "-10%" }}
            animate={{
              y: [0, 800],
              x: [0, (Math.random() - 0.5) * 100],
              rotate: [0, 360],
              opacity: [1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              delay: Math.random() * 2,
              repeat: Infinity,
              repeatDelay: 5,
            }}
          >
            {["🎈", "🎉", "✨", "🌟", "🎊"][Math.floor(Math.random() * 5)]}
          </motion.div>
        ))}
      </motion.div>

      {/* Page title — same structure/animations as Book 1 */}
      <div className="text-center pt-20 pb-10 relative z-10">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-5xl font-bold text-sky-900 mb-4"
        >
          🌲 The Forest of Wonder — Book 2 🌲
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-xl text-sky-700"
        >
          Click on the glowing icon below to start your next adventure
        </motion.p>
      </div>

      {/* Map container — same dimensions as Book 1 */}
      <div
        className="relative w-full mx-auto px-8"
        style={{ height: "calc(100vh - 250px)", minHeight: "600px", maxWidth: "1400px" }}
      >

        {/* Dashed SVG path lines — same stroke/dash as Book 1 (colour adapted for sky theme) */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 1 }}
        >
          {CHAPTER_LOCATIONS.slice(0, -1).map((loc, index) => {
            const next = CHAPTER_LOCATIONS[index + 1]!;
            return (
              <motion.line
                key={`path-${loc.id}`}
                x1={loc.position.x}
                y1={loc.position.y}
                x2={next.position.x}
                y2={next.position.y}
                stroke="#7dd3fc"
                strokeWidth="3"
                strokeDasharray="10,10"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.5 }}
                transition={{ duration: 1.5, delay: index * 0.3, ease: "easeInOut" }}
              />
            );
          })}
        </svg>

        {/* Static paw-print trail — EXACT MATCH of Book 1 including blue halo + blur fade-in */}
        {CHAPTER_LOCATIONS.slice(0, -1).map((loc, index) => {
          const next = CHAPTER_LOCATIONS[index + 1]!;

          const isCurrentPath =
            loc.id < currentChapter ||
            (loc.id === currentChapter && completedChapters.includes(loc.id));
          const shouldShowPath = isCurrentPath;

          const x1 = parseFloat(loc.position.x);
          const y1 = parseFloat(loc.position.y);
          const x2 = parseFloat(next.position.x);
          const y2 = parseFloat(next.position.y);
          const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);

          const numFootprints = 8; // matches Book 1

          return Array.from({ length: numFootprints + 1 }, (_, i) => {
            const progress = i / numFootprints;
            const x = x1 + (x2 - x1) * progress;
            const y = y1 + (y2 - y1) * progress;

            return (
              <motion.div
                key={`footprint-${loc.id}-${i}`}
                className="absolute pointer-events-none"
                style={{ left: `${x}%`, top: `${y}%`, zIndex: 2 }}
              >
                {/* Blue glowing halo — matches Book 1 exactly */}
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{
                    width: "40px",
                    height: "40px",
                    left: "-8px",
                    top: "-8px",
                    background:
                      "radial-gradient(circle, rgba(59,130,246,0.6) 0%, rgba(59,130,246,0.3) 50%, transparent 70%)",
                    filter: "blur(8px)",
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{
                    opacity: shouldShowPath ? [0, 1, 0.8, 1] : 0,
                    scale: shouldShowPath ? [0.5, 1.2, 1] : 0,
                  }}
                  transition={{
                    delay: 2.5 + index * 0.4 + i * 0.15,
                    duration: 1.2,
                    ease: "easeOut",
                  }}
                />

                {/* Paw print emoji — blur fade-in matches Book 1 */}
                <motion.div
                  className="relative text-2xl"
                  style={{
                    filter: shouldShowPath
                      ? "drop-shadow(0 0 8px rgba(59,130,246,0.8))"
                      : "none",
                  }}
                  initial={{ opacity: 0, scale: 0, filter: "blur(4px)", rotate: angle }}
                  animate={{
                    opacity: shouldShowPath ? 0.7 : 0,
                    scale: shouldShowPath ? 1 : 0,
                    filter: shouldShowPath ? "blur(0px)" : "blur(4px)",
                    rotate: angle,
                  }}
                  transition={{
                    delay: 2.5 + index * 0.4 + i * 0.15,
                    duration: 1.2,
                    ease: "easeOut",
                  }}
                >
                  🐾
                </motion.div>
              </motion.div>
            );
          });
        }).flat()}

        {/* Walking footprints animation — exact match of Book 1 (golden glow, same easing) */}
        <AnimatePresence>
          {isWalking &&
            walkingPath &&
            getWalkingFootprints().map((fp, index) => (
              <motion.div
                key={`walking-${index}`}
                className="absolute pointer-events-none"
                style={{ left: `${fp.x}%`, top: `${fp.y}%`, zIndex: 20 }}
                initial={{ opacity: 0, scale: 0, rotate: fp.angle }}
                animate={{ opacity: [0, 1, 0.9], scale: [0, 1.2, 1], y: [0, -5, 0] }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ delay: fp.delay, duration: 0.5, ease: "easeOut" }}
              >
                {/* Golden glow halo — matches Book 1 exactly */}
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{
                    width: "50px",
                    height: "50px",
                    left: "-12px",
                    top: "-12px",
                    background:
                      "radial-gradient(circle, rgba(251,191,36,0.8) 0%, rgba(251,191,36,0.4) 50%, transparent 70%)",
                    filter: "blur(10px)",
                  }}
                  animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
                  transition={{ duration: 0.6, repeat: 2, ease: "easeInOut" }}
                />
                {/* Golden paw print — matches Book 1 exactly */}
                <motion.div
                  className="relative text-3xl"
                  style={{ filter: "drop-shadow(0 0 12px rgba(251,191,36,1))" }}
                >
                  🐾
                </motion.div>
              </motion.div>
            ))}
        </AnimatePresence>

        {/* Chapter location markers — matches Book 1 structure exactly */}
        {CHAPTER_LOCATIONS.map((loc, index) => {
          const isCompleted = completedChapters.includes(loc.id);
          const isCurrent   = currentChapter === loc.id;
          const isHovered   = hoveredChapter === loc.id;

          // Per-chapter background colours (matches the colour identity of each chapter)
          const bgClass = isCurrent
            ? "bg-sky-200 shadow-2xl"
            : [
                "bg-sky-100",
                "bg-amber-100",
                "bg-rose-100",
                "bg-orange-100",
                "bg-teal-100",
                "bg-purple-100",
              ][index] ?? "bg-cream-100";

          return (
            <motion.div
              key={`chapter-${loc.id}`}
              className="absolute"
              style={{
                left: loc.position.x,
                top: loc.position.y,
                transform: "translate(-50%, -50%)",
                zIndex: 10,
              }}
              initial={{ opacity: 0, scale: 0, y: -50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: index * 0.3 + 0.5, type: "spring", stiffness: 200 }}
            >
              <motion.button
                onClick={() => handleChapterClick(loc.route, loc.id)}
                onMouseEnter={() => setHoveredChapter(loc.id)}
                onMouseLeave={() => setHoveredChapter(null)}
                className="relative group cursor-pointer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                disabled={isWalking}
              >
                {/* Glowing halo for current chapter — matches Book 1 (golden hue) */}
                {isCurrent && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-golden-400/30"
                    style={{ width: "120px", height: "120px", left: "-10px", top: "-10px" }}
                    animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  />
                )}

                {/* Emoji icon with floating animation — matches Book 1 exactly */}
                <motion.div
                  className={`
                    relative w-24 h-24 rounded-full flex items-center justify-center text-6xl
                    transition-all duration-300 shadow-lg group-hover:shadow-2xl
                    ${bgClass}
                    ${isCompleted ? "opacity-100" : "opacity-90"}
                  `}
                  animate={{ y: [-8, 8, -8], rotate: [-2, 2, -2] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: index * 0.5 }}
                >
                  {loc.emoji}

                  {/* Completion checkmark — matches Book 1 exactly */}
                  {isCompleted && (
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg">
                      ✓
                    </div>
                  )}
                </motion.div>

                {/* Hover / current info card — matches Book 1 exactly */}
                {(isHovered || isCurrent) && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute top-full mt-4 left-1/2 -translate-x-1/2 w-64 pointer-events-none"
                  >
                    <Card className="bg-white/95 backdrop-blur-sm border-sky-200 shadow-xl">
                      <div className="p-4 text-center">
                        <Badge className="mb-2 bg-sky-700 text-white">{loc.title}</Badge>
                        <p className="text-base font-semibold text-sky-900">{loc.subtitle}</p>
                        {isCurrent && (
                          <p className="text-xs text-amber-600 mt-2 font-medium">← You are here</p>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                )}
              </motion.button>
            </motion.div>
          );
        })}
      </div>

      {/* Footer — matches Book 1 exactly */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="text-center pb-10 mt-10"
      >
        <p className="text-sky-700 text-lg">✨ Follow the paw prints to discover each chapter ✨</p>
      </motion.div>
    </div>
  );
}
